import { Component, ElementRef, OnInit, Signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { select, Store } from '@ngxs/store';
import { ButtonModule, DialogModule, DialogService, InputModule, SnackbarModule, SnackbarService } from '@sag-ui';
import { UserState } from './state/user-management.state';
import { DeleteUserDialogComponent } from './dialogs/delete-user/delete-user.component';
import { EditUserDialogComponent } from './dialogs/edit-user/edit-user.component';
import { AddUserDialogComponent } from './dialogs/add-user/add-user.component';
import {
    CreateUser,
    DeleteUser,
    LazyLoadUsers,
    LoadUsers,
    SetSearchTerm,
    UpdateUser
} from './state/user-management.action';
import { ICreateUser, IPatchUser, IUser } from '@cod-monorepo/interfaces';
import { catchError, debounceTime, EMPTY, filter, fromEvent, switchMap, tap } from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { scrollOverAmountElement } from '../shared/utils/tiny-helpers';

@Component({
    selector: 'app-user-management',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        TranslateModule,
        RouterModule,
        InputModule,
        DialogModule,
        FormsModule,
        ReactiveFormsModule,
        SnackbarModule
    ],
    templateUrl: './user-management.component.html'
})
export class UserManagementComponent implements OnInit {
    public users: Signal<IUser[]> = select(UserState.users);
    public isLoading: Signal<boolean> = select(UserState.isLoading);
    public allUsersLoaded: Signal<boolean> = select(UserState.allUsersLoaded);
    public isLazyLoading: Signal<boolean> = select(UserState.isLazyLoading);
    public searchControl = new FormControl('');

    @ViewChild('table', { static: true }) table!: ElementRef;

    constructor(private dialogService: DialogService, private store: Store, private snackbar: SnackbarService) {}

    ngOnInit(): void {
        this.loadUsers();
        this.initSearch();
        this.initScrollListener();
    }

    public deleteUser(user: IUser): void {
        const dialogRef = this.dialogService.open<string, { name: string }>(DeleteUserDialogComponent, {
            width: '500px',
            data: {
                name: `${user.firstName} ${user.lastName}`
            }
        });

        dialogRef.afterClosed
            .pipe(
                filter(value => !!value),
                switchMap(() => this.store.dispatch(new DeleteUser(user.gid)))
            )
            .subscribe();
    }

    public addUser(): void {
        const dialogRef = this.dialogService.open<ICreateUser, void>(AddUserDialogComponent, {
            width: '500px'
        });

        dialogRef.afterClosed
            .pipe(
                filter(value => !!value),
                switchMap(value => this.store.dispatch(new CreateUser(value))),
                catchError(error => {
                    if (error.status === 400) {
                        this.snackbar.open({
                            title: 'User already exists',
                            description: 'User was not added, because the user already exists',
                            type: 'error',
                            duration: 5000,
                            position: 'bottom'
                        });
                    }
                    return EMPTY;
                })
            )
            .subscribe();
    }

    public editUser(user: IUser): void {
        const dialogRef = this.dialogService.open<IPatchUser, IUser>(EditUserDialogComponent, {
            width: '500px',
            data: user
        });

        dialogRef.afterClosed
            .pipe(
                filter(value => !!value),
                switchMap(value => this.store.dispatch(new UpdateUser(user.gid, value)))
            )
            .subscribe();
    }

    private initSearch(): void {
        this.searchControl.valueChanges
            .pipe(
                debounceTime(500),
                filter(value => value !== null),
                switchMap(value => this.store.dispatch(new SetSearchTerm(value as string)))
            )
            .subscribe();
    }

    private loadUsers(): void {
        this.store.dispatch(new LoadUsers());
    }

    private initScrollListener(): void {
        fromEvent<Event>(this.table.nativeElement, 'scroll')
            .pipe(
                filter(() => !this.isLazyLoading()),
                tap((event: Event) => {
                    if (!scrollOverAmountElement(event) || this.allUsersLoaded()) {
                        return;
                    }

                    this.store.dispatch(new LazyLoadUsers());
                })
            )
            .subscribe();
    }
}
